import React, { useState, useEffect, useRef, memo } from 'react';
import {
  Card, Row, Col, Select, Slider, Input, InputNumber, Button, Space, Typography, Divider,
  Switch, Tag, message, Spin, Alert, Modal, Tooltip, Upload
} from 'antd';
import {
  PlayCircleOutlined, ClearOutlined, SaveOutlined, CopyOutlined,
  SettingOutlined, MessageOutlined, LoadingOutlined, ThunderboltOutlined, HeartOutlined,
  ExperimentOutlined, RightOutlined, UserOutlined, PictureOutlined, AudioOutlined,
  VideoCameraOutlined, SearchOutlined, SoundOutlined, SortAscendingOutlined, InboxOutlined,
  CloseCircleOutlined, CheckOutlined, DeleteOutlined, ReloadOutlined, NodeExpandOutlined,
  ArrowRightOutlined, BranchesOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { chatAPI, multimodalAPI, modelAPI, userModelConfigAPI, presetAPI } from '../services/api';
import { t } from '../utils/i18n';
import { copyToClipboard } from '../utils/clipboard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 微信风格音频播放器组件
const WechatAudioPlayer = ({ audioUrl }) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlay = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play();
        setPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      if (audioRef.current.ended) {
        setPlaying(false);
      }
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
      />
      <div
        onClick={handlePlay}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          padding: '8px 12px',
          background: playing ? '#07c160' : '#f7f7f7',
          borderRadius: 20,
          minWidth: 120,
          transition: 'all 0.2s',
        }}
      >
        {playing ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#07c160">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                width: 3,
                height: playing ? (8 + Math.random() * 12) : 8,
                backgroundColor: playing ? '#fff' : '#999',
                borderRadius: 1,
                transition: 'height 0.1s',
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: 12, color: playing ? '#fff' : '#666', marginLeft: 4 }}>
          {formatDuration(duration)}
        </span>
      </div>
    </>
  );
};

// 预处理 LaTeX 格式
const preprocessLatex = (content) => {
  if (!content) return content;
  return content
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, p1) => `$$${p1}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, p1) => `$${p1}$`);
};

// 格式化时间
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

// 增强的消息项组件（支持 Markdown、代码高亮、媒体）
const EnhancedMessageItem = memo(({ msg, modelName, onCopy, onDelete, onRetry, isLoading }) => {
  const isUser = msg.role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
    await copyToClipboard(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (onCopy) onCopy(content);
  };

  return (
    <div className={`chat-message ${msg.role}`}>
      <div className="message-header-row">
        {!isUser && (
          <div className="message-avatar assistant-avatar">
            <span>🤖</span>
          </div>
        )}
        {!isUser && (
          <div className="message-info">
            <span className="message-name">{modelName}</span>
            {msg.timestamp && (
              <span className="message-time">{formatTime(msg.timestamp)}</span>
            )}
          </div>
        )}
        {isUser && (
          <div className="message-header-user">
            {msg.timestamp && (
              <span className="message-time">{formatTime(msg.timestamp)} <span className="message-name-inline">{t('me')}</span></span>
            )}
          </div>
        )}
        {isUser && (
          <div className="message-avatar user-avatar">
            <img src={require('../utils/img/logo.png')} alt={t('user_avatar')} />
          </div>
        )}
      </div>
      <div className={`message-row ${msg.role}`}>
        {!isUser && <div className="avatar-placeholder"></div>}
        <div className="message-body">
          <div className="message-content">
            {isLoading && !msg.content ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <LoadingOutlined style={{ fontSize: 16, color: '#1677ff' }} />
                <span style={{ color: '#666', fontSize: 13 }}>
                  {t('generating') || '生成中...'}
                </span>
                <span style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#1677ff',
                  marginLeft: 4,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
              </div>
            ) : msg.content ? (
              isUser ? (
                <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    h1: ({ children }) => <h1 style={{ fontSize: 20, marginBottom: 12, marginTop: 16 }}>{children}</h1>,
                    h2: ({ children }) => <h2 style={{ fontSize: 18, marginBottom: 10, marginTop: 14 }}>{children}</h2>,
                    h3: ({ children }) => <h3 style={{ fontSize: 16, marginBottom: 8, marginTop: 12 }}>{children}</h3>,
                    p: ({ children }) => <p style={{ marginBottom: 8, lineHeight: 1.6 }}>{children}</p>,
                    ul: ({ children }) => <ul style={{ marginLeft: 20, marginBottom: 8 }}>{children}</ul>,
                    ol: ({ children }) => <ol style={{ marginLeft: 20, marginBottom: 8 }}>{children}</ol>,
                    li: ({ children }) => <li style={{ marginBottom: 4, lineHeight: 1.5 }}>{children}</li>,
                    code: ({ inline, className, children, ...props }) => {
                      const match = /language-(\w+)/.exec(className || '');
                      const language = match ? match[1] : '';
                      const codeString = String(children).replace(/\n$/, '');

                      if (!inline && (language || codeString.includes('\n'))) {
                        return (
                          <SyntaxHighlighter
                            style={oneLight}
                            language={language || 'text'}
                            PreTag="div"
                            customStyle={{
                              margin: '0 0 8px 0',
                              borderRadius: 6,
                              fontSize: 13,
                            }}
                            {...props}
                          >
                            {codeString}
                          </SyntaxHighlighter>
                        );
                      }

                      return (
                        <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: 13, color: '#d63384' }} {...props}>
                          {children}
                        </code>
                      );
                    },
                    pre: ({ children }) => <div style={{ marginBottom: 8 }}>{children}</div>,
                    blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #1677ff', paddingLeft: 12, marginLeft: 0, color: '#666', marginBottom: 8 }}>{children}</blockquote>,
                    strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                    hr: () => <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />,
                    table: ({ children }) => <div style={{ overflowX: 'auto', marginBottom: 8 }}><table style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table></div>,
                    th: ({ children }) => <th style={{ border: '1px solid #e5e7eb', padding: '8px 12px', background: '#f9fafb', textAlign: 'left' }}>{children}</th>,
                    td: ({ children }) => <td style={{ border: '1px solid #e5e7eb', padding: '8px 12px' }}>{children}</td>,
                    a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1677ff' }}>{children}</a>,
                  }}
                >
                  {preprocessLatex(msg.content)}
                </ReactMarkdown>
              )
            ) : null}
          </div>
          
          {/* 图片预览 */}
          {msg.images && msg.images.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {msg.images.map((img, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  <img 
                    src={img.startsWith('data:') ? img : img} 
                    alt={`Image ${idx + 1}`} 
                    style={{ maxWidth: 300, maxHeight: 300, borderRadius: 8 }}
                    onClick={() => window.open(img, '_blank')}
                  />
                </div>
              ))}
            </div>
          )}

          {/* 音频播放 */}
          {msg.audioUrl && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <WechatAudioPlayer audioUrl={msg.audioUrl} />
            </div>
          )}
          
          {/* 操作按钮 */}
          <div className="message-actions">
            <Tooltip title={copied ? (t('copied') || 'Copied') : (t('copy') || 'Copy')}>
              <button className="message-action-btn" onClick={handleCopy}>
                {copied ? <CheckOutlined /> : <CopyOutlined />}
              </button>
            </Tooltip>
            {!isUser && (
              <Tooltip title={t('regenerate') || 'Regenerate'}>
                <button className="message-action-btn" onClick={onRetry}>
                  <ReloadOutlined />
                </button>
              </Tooltip>
            )}
            <Tooltip title={t('delete') || 'Delete'}>
              <button className="message-action-btn" onClick={onDelete}>
                <DeleteOutlined />
              </button>
            </Tooltip>
          </div>
        </div>
        {isUser && <div className="avatar-placeholder"></div>}
      </div>
    </div>
  );
});

// 模式类型配置
const TYPE_MAPPING = {
  text: { label: { zh: '对话', en: 'Chat' }, icon: 'chat' },
  image: { label: { zh: '图像', en: 'Image' }, icon: 'image' },
  embeddings: { label: { zh: '嵌入', en: 'Embeddings' }, icon: 'embeddings' },
  audio: { label: { zh: '语音', en: 'Audio' }, icon: 'audio' },
  video: { label: { zh: '视频', en: 'Video' }, icon: 'video' },
  rerank: { label: { zh: '重排序', en: 'Rerank' }, icon: 'rerank' },
  speech: { label: { zh: '语音合成', en: 'Speech' }, icon: 'speech' },
  transcription: { label: { zh: '转录', en: 'Transcription' }, icon: 'transcription' },
};

const Playground = () => {
  // 状态
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [selectedType, setSelectedType] = useState('text'); // text, image, embeddings, audio, video, rerank, speech, transcription
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [abortController, setAbortController] = useState(null);
  const [myTrainedConfigs, setMyTrainedConfigs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [userTrainedConfig, setUserTrainedConfig] = useState(null); // 当前使用的训练配置
  const [userTrainedParams, setUserTrainedParams] = useState(null); // 训练配置中的参数
  const selectedModelRef = useRef('');
  const selectedProviderRef = useRef('all');

  // 参数配置
  const [params, setParams] = useState({
    temperature: 0.7,
    max_tokens: 2048,
    top_p: 1,
    frequency_penalty: 0,
    presence_penalty: 0,
    stream: true,
  });

  // 附件状态
  const [attachments, setAttachments] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const messagesEndRef = useRef(null);

  // 保存训练模型模态框
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveForm, setSaveForm] = useState({
    name: '',
    description: '',
    isPublic: false,
  });

  // 数据流侧边栏
  const [dataFlowVisible, setDataFlowVisible] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [currentUsage, setCurrentUsage] = useState(null); // 当前请求的用量

  // 同步 ref
  useEffect(() => { selectedModelRef.current = selectedModel; }, [selectedModel]);
  useEffect(() => { selectedProviderRef.current = selectedProvider; }, [selectedProvider]);

  // 提取版本号用于排序
  const extractVersion = (modelName) => {
    const match = modelName.match(/[_\-]?v?(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  // 获取类型图标
  const getTypeIcon = (type) => {
    switch (type) {
      case 'text': return <MessageOutlined />;
      case 'image': return <PictureOutlined />;
      case 'embeddings': return <SearchOutlined />;
      case 'audio': return <AudioOutlined />;
      case 'video': return <VideoCameraOutlined />;
      case 'rerank': return <SortAscendingOutlined />;
      case 'speech': return <SoundOutlined />;
      case 'transcription': return <AudioOutlined />;
      default: return <MessageOutlined />;
    }
  };

  // 获取类型标签（支持中英文）
  const getTypeLabel = (type) => {
    const config = TYPE_MAPPING[type];
    if (!config) return type;
    // 检查是否是多语言对象
    if (typeof config.label === 'object') {
      const lang = localStorage.getItem('lang') || 'en';
      return lang === 'en' ? (config.label.en || type) : (config.label.zh || type);
    }
    return config.label;
  };

  // 获取某类型模型数量
  const getTypeModelCount = (type) => {
    return models.filter(m => m.type === type).length;
  };

  // 获取过滤后的供应商列表（根据类型）
  const getFilteredProviders = () => {
    const filteredByType = models.filter(m => m.type === selectedType);
    const providerMap = new Map();
    filteredByType.forEach(m => {
      if (m.provider) {
        const name = typeof m.provider === 'object' ? m.provider.display_name : m.provider;
        if (name && !providerMap.has(name)) {
          providerMap.set(name, true);
        }
      }
    });
    return Array.from(providerMap.keys()).sort();
  };

  // 获取过滤并排序的模型列表
  const getFilteredModels = () => {
    let filtered = models.filter(m => m.type === selectedType);
    
    if (selectedProvider !== 'all') {
      filtered = filtered.filter(m => {
        const providerName = typeof m.provider === 'object' ? m.provider?.display_name : m.provider;
        return providerName === selectedProvider;
      });
    }
    
    // 排序
    if (selectedProvider === 'all') {
      return [...filtered].sort((a, b) => (a.model_name || '').localeCompare(b.model_name || ''));
    }
    return [...filtered].sort((a, b) => {
      const vA = extractVersion(a.model_name);
      const vB = extractVersion(b.model_name);
      if (vB !== vA) return vB - vA;
      return (a.model_name || '').localeCompare(b.model_name || '');
    });
  };

  const filteredProviders = getFilteredProviders();
  const filteredModels = getFilteredModels();

  // 加载模型列表
  useEffect(() => {
    const loadModels = async () => {
      try {
        const res = await modelAPI.getAvailable();
        if (res.data) {
          // 处理两种数据格式：数组 或 { list: [...] } 或 { models: [...] }
          let modelList = [];
          if (Array.isArray(res.data)) {
            modelList = res.data;
          } else if (res.data.list) {
            modelList = res.data.list;
          } else if (res.data.models) {
            modelList = res.data.models;
          }
          setModels(modelList);
          
          // 初始选择第一个可用模型
          const textModels = modelList.filter(m => m.type === 'text');
          if (textModels.length > 0) {
            const sorted = [...textModels].sort((a, b) => (a.model_name || '').localeCompare(b.model_name || ''));
            setSelectedModel(sorted[0].model_name);
            selectedModelRef.current = sorted[0].model_name;
          }
        }
      } catch (err) {
        console.error('Failed to load models:', err);
        message.error(t('load_models_failed', '加载模型列表失败'));
      }
    };
    loadModels();
  }, []);

  // 加载预设角色
  useEffect(() => {
    const loadPresets = async () => {
      try {
        const myRes = await presetAPI.getMyPresets({ page: 1, page_size: 20 });
        const publicRes = await presetAPI.getPublicPresets({ page: 1, page_size: 20 });
        
        const myItems = myRes.data?.items || [];
        const publicItems = publicRes.data?.items || [];
        
        const allPresets = [...myItems, ...publicItems];
        const uniquePresets = allPresets.filter((preset, index, self) => 
          index === self.findIndex(p => p.id === preset.id)
        );
        
        setPresets(uniquePresets.map(p => ({
          value: p.id,
          label: p.name,
          systemPrompt: p.prompt || p.system_prompt || p.content || '',
          description: p.description || '',
        })));
        
        // 如果有 pending 的 system_prompt，匹配对应的预设
        if (window._pendingSystemPrompt && uniquePresets.length > 0) {
          const matchedPreset = uniquePresets.find(p => 
            (p.prompt || p.system_prompt || p.content || '') === window._pendingSystemPrompt
          );
          if (matchedPreset) {
            setSelectedPreset({
              value: matchedPreset.id,
              label: matchedPreset.name,
              systemPrompt: matchedPreset.prompt || matchedPreset.system_prompt || matchedPreset.content || '',
              description: matchedPreset.description || '',
            });
          }
          window._pendingSystemPrompt = null;
        }
      } catch (err) {
        console.error('Failed to load presets:', err);
      }
    };
    loadPresets();
  }, []);

  // 加载我的训练配置列表
  useEffect(() => {
    const loadMyConfigs = async () => {
      try {
        const res = await userModelConfigAPI.getMyConfigs({ page: 1, page_size: 20 });
        if (res.code === 0 && res.data.items) {
          setMyTrainedConfigs(res.data.items);
        }
      } catch (err) {
        console.error('Failed to load trained configs:', err);
      }
    };
    loadMyConfigs();

    // 监听配置删除事件
    const handleConfigChange = () => {
      loadMyConfigs();
    };
    window.addEventListener('trainedConfigChanged', handleConfigChange);
    
    // 监听 localStorage 变化（跨页面同步）
    const handleStorageChange = (e) => {
      if (e.key === 'trained_configs_version') {
        loadMyConfigs();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('trainedConfigChanged', handleConfigChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 从模型广场继续训练 - 加载配置
  const savedConfig = localStorage.getItem('playground_trained_config');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      const configParams = config.parameters || {};
      
      // 设置用户训练配置状态
      setUserTrainedConfig(config);
      setUserTrainedParams(configParams);
      
      // 保存到 localStorage 供 Dashboard 使用
      localStorage.setItem('selected_trained_model', JSON.stringify({
        configName: config.configName || config.name,
        model_name: config.modelName || config.model_name,
        parameters: configParams,
        presetName: config.presetName || '',
        presetSystemPrompt: config.presetSystemPrompt || configParams.system_prompt || '',
        presetId: config.presetId || '',
      }));
      
      setParams(p => ({
        ...p,
        temperature: configParams.temperature ?? p.temperature,
        max_tokens: configParams.max_tokens ?? p.max_tokens,
        top_p: configParams.top_p ?? p.top_p,
        frequency_penalty: configParams.frequency_penalty ?? p.frequency_penalty,
        presence_penalty: configParams.presence_penalty ?? p.presence_penalty,
      }));
      if (configParams.system_prompt) {
        setSystemPrompt(configParams.system_prompt);
        // 标记需要匹配的 system_prompt，稍后在 presets 加载后匹配
        window._pendingSystemPrompt = configParams.system_prompt;
      }
      setSelectedModel(config.modelName || config.model_name);
      selectedModelRef.current = config.modelName || config.model_name;
      localStorage.removeItem('playground_trained_config');
    } catch (err) {
      console.error('Failed to parse saved config:', err);
    }
  }

  // 自动滚动
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 处理预设选择
  const handlePresetChange = (presetId) => {
    if (!presetId) {
      setSelectedPreset(null);
      setSystemPrompt('');
      return;
    }
    const preset = presets.find(p => p.value === presetId);
    if (preset) {
      setSelectedPreset(preset);
      setSystemPrompt(preset.systemPrompt);
    }
  };

  // 处理类型切换
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedProvider('all');
    selectedProviderRef.current = 'all';
    
    // 筛选该类型下的第一个模型
    const typeModels = models.filter(m => m.type === type);
    if (typeModels.length > 0) {
      const sorted = [...typeModels].sort((a, b) => (a.model_name || '').localeCompare(b.model_name || ''));
      setSelectedModel(sorted[0].model_name);
      selectedModelRef.current = sorted[0].model_name;
    } else {
      setSelectedModel('');
      selectedModelRef.current = '';
    }
  };

  // 处理供应商变化
  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    selectedProviderRef.current = value;
    
    const filtered = models.filter(m => m.type === selectedType);
    let providerModels = value === 'all' ? filtered : filtered.filter(m => {
      const providerName = typeof m.provider === 'object' ? m.provider?.display_name : m.provider;
      return providerName === value;
    });
    
    if (providerModels.length > 0) {
      if (value === 'all') {
        providerModels.sort((a, b) => (a.model_name || '').localeCompare(b.model_name || ''));
      } else {
        providerModels.sort((a, b) => {
          const vA = extractVersion(a.model_name);
          const vB = extractVersion(b.model_name);
          if (vB !== vA) return vB - vA;
          return (a.model_name || '').localeCompare(b.model_name || '');
        });
      }
      setSelectedModel(providerModels[0].model_name);
      selectedModelRef.current = providerModels[0].model_name;
    } else {
      setSelectedModel('');
      selectedModelRef.current = '';
    }
  };

  // 发送消息
  const handleSend = async () => {
    if (!input.trim() && attachments.length === 0) return;
    if (!selectedModelRef.current) {
      message.warning(t('select_model_first', '请先选择一个模型'));
      return;
    }

    // 构建消息内容（支持文本和图片）
    let userMessage;
    if (attachments.length > 0 && selectedType === 'image') {
      // 多模态消息（图片+文字）
      userMessage = {
        role: 'user',
        content: [
          ...attachments.map(att => ({
            type: 'image_url',
            image_url: { url: att.url }
          })),
          ...(input.trim() ? [{ type: 'text', text: input }] : [])
        ]
      };
    } else {
      userMessage = { role: 'user', content: input };
    }

    setMessages(prev => [...prev, { ...userMessage, timestamp: Date.now() }]);
    setInput('');
    setAttachments([]); // 清空附件
    setLoading(true);

    // 添加空的助手消息占位（索引 = 当前消息数量，即用户消息之后的位置）
    setMessages(prev => {
      const assistantMessageIndex = prev.length;
      return [...prev, { role: 'assistant', content: '', timestamp: Date.now(), assistantIndex: assistantMessageIndex }];
    });
    let streamContent = '';

    const request = {
      model: selectedModelRef.current,
      messages: [
        ...(systemPrompt ? [{ role: 'system', content: systemPrompt }] : []),
        ...messages,
        userMessage,
      ],
      stream: params.stream,
      // 基础参数始终传递
      temperature: userTrainedParams?.temperature ?? params.temperature,
      max_tokens: userTrainedParams?.max_tokens ?? params.max_tokens,
      top_p: userTrainedParams?.top_p ?? params.top_p,
      frequency_penalty: userTrainedParams?.frequency_penalty ?? params.frequency_penalty,
      presence_penalty: userTrainedParams?.presence_penalty ?? params.presence_penalty,
    };

    // 保存请求数据用于数据流面板展示
    setRequestData(request);
    setResponseData(null); // 清空之前的响应数据

    try {
      if (params.stream) {
        // 流式响应
        let usageData = null;
        const { abort, promise } = chatAPI.completionsStream(
          request,
          (chunk) => {
            // 处理流式响应
            if (chunk.choices && chunk.choices[0]) {
              const delta = chunk.choices[0].delta?.content || '';
              if (delta) {
                streamContent += delta;
                setMessages((prev) => {
                  // 找到最后一个 assistant 消息（通过 assistantIndex 标识）
                  const lastAssistantIndex = [...prev].reverse().findIndex(m => m.role === 'assistant');
                  if (lastAssistantIndex !== -1) {
                    const actualIndex = prev.length - 1 - lastAssistantIndex;
                    const newMessages = [...prev];
                    if (newMessages[actualIndex]) {
                      newMessages[actualIndex] = {
                        ...newMessages[actualIndex],
                        content: streamContent,
                      };
                    }
                    return newMessages;
                  }
                  return prev;
                });
                // 滚动到底部
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }
            }
            // 捕获 usage 数据
            if (chunk.usage) {
              usageData = chunk.usage;
            }
          },
          (err) => {
            // 错误处理
            setLoading(false);
            const errorMsg = err?.message || t('request_failed', '请求失败');
            if (err?.name !== 'AbortError' && errorMsg !== 'canceled') {
              message.error(errorMsg);
              setMessages(prev => prev.slice(0, -1));
            }
          },
          () => {
            // 完成处理 - 保存响应数据
            setResponseData({
              content: streamContent,
              usage: usageData
            });
            setCurrentUsage(usageData);
            setLoading(false);
          }
        );

        setAbortController({ abort });
        await promise;
      } else {
        // 非流式响应
        try {
          const response = await chatAPI.completions(request);
          setLoading(false);
          
          console.log('[Non-Stream] Response:', response);
          
          // 尝试多种可能的数据结构
          const choices = response.data?.choices || response.choices || response?.data?.data?.choices;
          const messageContent = response.data?.choices?.[0]?.message?.content
            || response.choices?.[0]?.message?.content
            || response.data?.data?.choices?.[0]?.message?.content
            || '';
          const usage = response.data?.usage || response.usage || null;

          console.log('[Non-Stream] Choices:', choices);
          console.log('[Non-Stream] Content:', messageContent);

          // 保存响应数据用于数据流面板展示
          setResponseData({
            content: messageContent,
            usage: usage
          });
          setCurrentUsage(usage);

          if (messageContent) {
            // 更新最后一条 assistant 消息
            setMessages(prev => {
              const newMessages = [...prev];
              if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant') {
                newMessages[newMessages.length - 1] = {
                  ...newMessages[newMessages.length - 1],
                  content: messageContent,
                };
              }
              return newMessages;
            });
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        } catch (err) {
          setLoading(false);
          // 保存错误响应
          setResponseData({
            error: err.message || 'Request failed'
          });
          console.error('[Non-Stream] Error:', err);
          throw err;
        }
      }
    } catch (err) {
      setLoading(false);
      setAbortController(null);
      // 保存错误响应
      setResponseData({
        error: err.message || 'Request failed'
      });
      if (err.name !== 'AbortError') {
        console.error('Request failed:', err);
        message.error(err.message || t('request_failed', '请求失败'));
        setMessages(prev => prev.slice(0, -1));
      }
    }
  };

  // 取消请求
  const handleCancel = () => {
    if (abortController) {
      if (typeof abortController.abort === 'function') {
        abortController.abort();
      }
    }
    setLoading(false);
  };

  // 清空对话
  const handleClear = () => {
    setMessages([]);
    message.success(t('dialogue_cleared', '对话已清空'));
  };

  // 复制消息
  const handleCopy = async (content) => {
    if (content) {
      const success = await copyToClipboard(content);
      if (success) {
        message.success(t('copied_to_clipboard', '已复制到剪贴板'));
      }
    }
  };

  // 保存对话
  const handleSave = async () => {
    if (messages.length === 0) return;
    try {
      const response = await chatAPI.saveConversation({
        title: messages[0]?.content?.substring(0, 50) || 'Untitled',
        messages: messages,
        model: selectedModelRef.current,
      });
      if (response.code === 0 || response.data) {
        message.success(t('conversation_saved', '对话已保存'));
      }
    } catch (err) {
      console.error('Save conversation failed:', err);
    }
  };

  // 保存为训练模型
  const handleSaveAsTrainedModel = async () => {
    if (!saveForm.name.trim()) {
      message.warning(t('enter_config_name', '请输入模型名称'));
      return;
    }

    try {
      const response = await userModelConfigAPI.createConfig({
        name: saveForm.name,
        description: saveForm.description,
        model_name: selectedModelRef.current,
        parameters: JSON.stringify({
          temperature: params.temperature,
          max_tokens: params.max_tokens,
          top_p: params.top_p,
          frequency_penalty: params.frequency_penalty,
          presence_penalty: params.presence_penalty,
          system_prompt: systemPrompt,
        }),
        is_public: saveForm.isPublic,
      });

      if (response.code === 0) {
        message.success(t('save_success', '训练模型已保存'));
        setSaveModalVisible(false);
        
        // 刷新我的训练列表
        const res = await userModelConfigAPI.getMyConfigs({ page: 1, page_size: 20 });
        if (res.code === 0 && res.data.items) {
          setMyTrainedConfigs(res.data.items);
        }
      }
    } catch (err) {
      console.error('保存失败:', err);
      message.error(err.response?.data?.message || t('save_failed', '保存失败'));
    }
  };

  // 重置保存表单
  const resetSaveForm = () => {
    setSaveForm({ name: '', description: '', isPublic: false });
  };

  // 加载训练配置参数
  const handleLoadTrainedConfig = (config) => {
    const configParams = config.parsed_params || JSON.parse(config.parameters || '{}');
    
    // 设置用户训练配置状态
    setUserTrainedConfig(config);
    setUserTrainedParams(configParams);
    
    // 匹配预设：查找 system_prompt 匹配的预设
    const matchedPreset = configParams.system_prompt
      ? presets.find(p => p.systemPrompt === configParams.system_prompt)
      : null;

    // 保存到 localStorage 供 Dashboard 使用
    localStorage.setItem('selected_trained_model', JSON.stringify({
      configName: config.name,
      model_name: config.model_name,
      parameters: configParams,
      presetName: matchedPreset?.label || '',
      presetSystemPrompt: matchedPreset?.systemPrompt || '',
      presetId: matchedPreset?.value || '',
    }));
    
    setParams(p => ({
      ...p,
      temperature: configParams.temperature ?? p.temperature,
      max_tokens: configParams.max_tokens ?? p.max_tokens,
      top_p: configParams.top_p ?? p.top_p,
      frequency_penalty: configParams.frequency_penalty ?? p.frequency_penalty,
      presence_penalty: configParams.presence_penalty ?? p.presence_penalty,
    }));
    if (configParams.system_prompt) {
      setSystemPrompt(configParams.system_prompt);
    }
    // 设置匹配的预设角色
    if (matchedPreset) {
      setSelectedPreset(matchedPreset);
    } else {
      setSelectedPreset(null);
    }
    setSelectedModel(config.model_name);
    selectedModelRef.current = config.model_name;
  };

  // 跳转到模型广场的我的训练
  const goToMyTraining = () => {
    window.location.href = '/dashboard/models?tab=my-training';
  };

  // 参数配置面板
  const ParameterPanel = () => (
    <Card 
      size="small" 
      title={<><SettingOutlined /> {t('parameter_config', '参数配置')}</>}
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      styles={{ body: { flex: 1, overflow: 'auto', padding: 12 } }}
    >
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Text style={{ fontSize: 12 }}>{t('temperature', 'Temperature')}</Text>
          <InputNumber
            size="small"
            min={0}
            max={2}
            step={0.1}
            value={params.temperature}
            onChange={(v) => setParams(p => ({ ...p, temperature: v ?? 0 }))}
            style={{ width: 60 }}
          />
        </div>
        <Slider
          min={0}
          max={2}
          step={0.1}
          value={params.temperature}
          onChange={(v) => setParams(p => ({ ...p, temperature: v }))}
          marks={{ 0: '0', 1: '1', 2: '2' }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Text style={{ fontSize: 12 }}>{t('max_tokens', 'Max Tokens')}</Text>
          <InputNumber
            size="small"
            min={64}
            max={8192}
            step={64}
            value={params.max_tokens}
            onChange={(v) => setParams(p => ({ ...p, max_tokens: v ?? 64 }))}
            style={{ width: 70 }}
          />
        </div>
        <Slider
          min={64}
          max={8192}
          step={64}
          value={params.max_tokens}
          onChange={(v) => setParams(p => ({ ...p, max_tokens: v }))}
          marks={{ 64: '64', 2048: '2K', 4096: '4K', 8192: '8K' }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Text style={{ fontSize: 12 }}>{t('top_p', 'Top P')}</Text>
          <InputNumber
            size="small"
            min={0}
            max={1}
            step={0.05}
            value={params.top_p}
            onChange={(v) => setParams(p => ({ ...p, top_p: v ?? 0 }))}
            style={{ width: 60 }}
          />
        </div>
        <Slider
          min={0}
          max={1}
          step={0.05}
          value={params.top_p}
          onChange={(v) => setParams(p => ({ ...p, top_p: v }))}
          marks={{ 0: '0', 0.5: '0.5', 1: '1' }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Text style={{ fontSize: 12 }}>{t('frequency_penalty', 'Frequency')}</Text>
          <InputNumber
            size="small"
            min={-2}
            max={2}
            step={0.1}
            value={params.frequency_penalty}
            onChange={(v) => setParams(p => ({ ...p, frequency_penalty: v ?? 0 }))}
            style={{ width: 60 }}
          />
        </div>
        <Slider
          min={-2}
          max={2}
          step={0.1}
          value={params.frequency_penalty}
          onChange={(v) => setParams(p => ({ ...p, frequency_penalty: v }))}
          marks={{ '-2': '-2', 0: '0', 2: '2' }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Text style={{ fontSize: 12 }}>{t('presence_penalty', 'Presence')}</Text>
          <InputNumber
            size="small"
            min={-2}
            max={2}
            step={0.1}
            value={params.presence_penalty}
            onChange={(v) => setParams(p => ({ ...p, presence_penalty: v ?? 0 }))}
            style={{ width: 60 }}
          />
        </div>
        <Slider
          min={-2}
          max={2}
          step={0.1}
          value={params.presence_penalty}
          onChange={(v) => setParams(p => ({ ...p, presence_penalty: v }))}
          marks={{ '-2': '-2', 0: '0', 2: '2' }}
        />
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Text style={{ fontSize: 12, lineHeight: '20px', margin: 0 }}>{t('stream_mode', 'Stream')}</Text>
        <Switch
          size="small"
          checked={params.stream}
          onChange={(v) => setParams(p => ({ ...p, stream: v }))}
        />
      </div>
    </Card>
  );

  return (
    <div style={{ padding: 16, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* 顶部标题栏 */}
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
          <ThunderboltOutlined /> {t('playground', '模型训练')}
        </Title>
        <Space size={4}>
          <Button size="small" icon={<HeartOutlined />} onClick={() => setSaveModalVisible(true)}>
            {t('save_training_model', '保存')}
          </Button>
          <Button size="small" icon={<ClearOutlined />} onClick={handleClear} danger disabled={messages.length === 0}>
            {t('clear', '清空')}
          </Button>
        </Space>
      </div>

      {/* 我的训练模型列表 - 垂直居中 */}
      {myTrainedConfigs.length > 0 && (
        <Card 
          size="small" 
          style={{ marginBottom: 8, flexShrink: 0 }}
          styles={{ body: { padding: '6px 12px', display: 'flex', alignItems: 'center' } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <ExperimentOutlined style={{ color: '#722ed1', fontSize: 12 }} />
            <span style={{ color: '#722ed1', fontSize: 12, fontWeight: 500 }}>
              {t('my_training', '我的训练')} ({myTrainedConfigs.length})
            </span>
            <div style={{ flex: 1, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {myTrainedConfigs.slice(0, 4).map(config => (
                <Tag 
                  key={config.id}
                  color="purple" 
                  style={{ cursor: 'pointer', fontSize: 11, margin: 0 }}
                  onClick={() => handleLoadTrainedConfig(config)}
                >
                  {config.name}
                </Tag>
              ))}
            </div>
            <Button type="link" size="small" onClick={goToMyTraining} style={{ padding: 0, fontSize: 11 }}>
              {t('view_all', '查看')} →
            </Button>
          </div>
        </Card>
      )}

      {myTrainedConfigs.length === 0 && (
        <Alert
          message={t('no_trained_models', '暂无训练模型')}
          description={
            <span>
              {t('save_training_model_tip', '调试好参数后保存配置')}
              <Link to="/dashboard/models?tab=my-training">{t('my_training', '我的训练')}</Link>
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 8, flexShrink: 0 }}
        />
      )}

      {/* 主内容区 - 填满剩余空间 */}
      <Row gutter={12} style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* 左侧 - 模型选择、角色和参数（整体可滚动） */}
        <Col span={6} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1, overflow: 'auto', paddingRight: 4 }}>
            {/* 模型选择 */}
            <Card size="small" style={{ marginBottom: 8 }}>
              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>{t('model_selection', '模型选择')}</Text>
              {/* 类型下拉 - 仅 chat 可选，其他显示但禁用 */}
              <Select
                style={{ width: '100%', marginBottom: 8 }}
                value={selectedType}
                onChange={handleTypeChange}
                size="small"
              >
                {Object.entries(TYPE_MAPPING).map(([type, config]) => {
                  const count = getTypeModelCount(type);
                  if (count === 0) return null;
                  const isChat = type === 'text';
                  return (
                    <Select.Option key={type} value={type} disabled={!isChat}>
                      {getTypeIcon(type)} {getTypeLabel(type)} ({count}){!isChat ? ` - ${t('training_only_chat', '仅对话支持训练')}` : ''}
                    </Select.Option>
                  );
                })}
              </Select>
              {/* 供应商下拉 */}
              <Select
                style={{ width: '100%', marginBottom: 8 }}
                value={selectedProvider}
                onChange={handleProviderChange}
                size="small"
              >
                <Select.Option key="all" value="all">
                  {t('all_providers', '全部厂商')}
                </Select.Option>
                {filteredProviders.map(p => (
                  <Select.Option key={p} value={p}>
                    {p}
                  </Select.Option>
                ))}
              </Select>
              {/* 模型下拉 */}
              <Select
                style={{ width: '100%' }}
                value={selectedModel}
                onChange={(value) => {
                  setSelectedModel(value);
                  selectedModelRef.current = value;
                }}
                size="small"
                showSearch
                optionFilterProp="children"
              >
                {filteredModels.map(m => (
                  <Select.Option key={m.model_name} value={m.model_name}>
                    {m.display_name || m.model_name}
                  </Select.Option>
                ))}
              </Select>
            </Card>

            {/* 角色/预设选择 */}
            {presets.length > 0 && (
              <Card size="small" style={{ marginBottom: 8 }}>
                <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
                  <UserOutlined /> {t('select_role', '选择角色')}
                </Text>
                <Select
                  style={{ width: '100%' }}
                  value={selectedPreset?.value}
                  onChange={handlePresetChange}
                  placeholder={t('select_role_placeholder', '选择角色')}
                  allowClear
                  size="small"
                  showSearch
                >
                  {presets.map(p => (
                    <Select.Option key={p.value} value={p.value}>
                      <Tooltip title={p.description}>
                        <span>{p.label}</span>
                      </Tooltip>
                    </Select.Option>
                  ))}
                </Select>
                {selectedPreset && (
                  <div style={{ marginTop: 4, fontSize: 11, color: '#666' }}>
                    {selectedPreset.description || t('no_description', '暂无描述')}
                  </div>
                )}
              </Card>
            )}

            {/* 参数配置 */}
            <ParameterPanel />
          </div>
        </Col>

        {/* 右侧 - 对话区域 */}
        <Col span={18} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* 用户训练配置指示器 */}
          {userTrainedConfig && (
            <div style={{ padding: '6px 12px', background: '#722ed1', color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ThunderboltOutlined />
                <span>{t('trained_model', '训练参数')}: {userTrainedConfig.name}</span>
              </div>
              <Button 
                type="text" 
                size="small" 
                icon={<CloseCircleOutlined />} 
                onClick={() => {
                  setUserTrainedConfig(null);
                  setUserTrainedParams(null);
                  localStorage.removeItem('selected_trained_model');
                  message.success(t('config_cleared', '已清除训练配置'));
                }}
                style={{ color: '#fff', padding: 0, height: 'auto', minWidth: 'auto' }}
              />
            </div>
          )}
          {/* 消息列表 */}
          <Card
            size="small"
            style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            styles={{ body: { flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', background: '#fafafa' } }}
          >
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#999', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <MessageOutlined style={{ fontSize: 48 }} />
                <Paragraph type="secondary" style={{ marginTop: 16 }}>
                  {t('start_conversation', '开始对话吧！选择一个模型，调整参数，然后发送消息。')}
                </Paragraph>
              </div>
            ) : (
              <div style={{ flex: 1, overflow: 'auto' }}>
                {messages.map((msg, idx) => (
                  <EnhancedMessageItem
                    key={idx}
                    msg={{ ...msg, timestamp: msg.timestamp || Date.now() }}
                    modelName={selectedModel || 'AI'}
                    onCopy={() => handleCopy(msg.content)}
                    onDelete={() => {
                      setMessages(prev => prev.filter((_, i) => i !== idx));
                      message.success(t('message_deleted', '消息已删除'));
                    }}
                    onRetry={() => {
                      // 删除当前助手消息，重新发送上一条用户消息
                      const userMsgIndex = messages.findIndex((m, i) => i < idx && m.role === 'user');
                      if (userMsgIndex !== -1) {
                        const userMsg = messages[userMsgIndex];
                        setMessages(prev => prev.filter((_, i) => i !== idx));
                        // 重新发送
                        setTimeout(() => {
                          setInput(userMsg.content || '');
                          // 触发发送
                        }, 100);
                      }
                    }}
                    isLoading={loading && idx === messages.length - 1 && !msg.content}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </Card>

          {/* 输入区域 */}
          <Card size="small" style={{ marginTop: 8, flexShrink: 0 }}>
            {/* 附件预览区域 */}
            {attachments.length > 0 && (
              <div style={{ marginBottom: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                {attachments.map(att => (
                  <div key={att.uid} style={{ display: 'inline-block', position: 'relative', marginRight: 8, marginBottom: 4 }}>
                    {att.type === 'image' ? (
                      <img 
                        src={att.url} 
                        alt={att.name}
                        style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                      />
                    ) : (
                      <div style={{ width: 60, height: 60, background: '#e6f7ff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AudioOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                      </div>
                    )}
                    <CloseCircleOutlined 
                      style={{ position: 'absolute', top: -6, right: -6, color: '#ff4d4f', fontSize: 16, cursor: 'pointer', background: '#fff', borderRadius: '50%' }}
                      onClick={() => setAttachments(prev => prev.filter(a => a.uid !== att.uid))}
                    />
                    <span style={{ position: 'absolute', bottom: -16, left: 0, right: 0, fontSize: 10, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('input_placeholder', '输入消息... (Shift+Enter 换行，Enter 发送)')}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <Tooltip title={t('upload_image', '上传图片')}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const newAttachment = {
                          uid: Date.now(),
                          name: file.name,
                          url: e.target.result,
                          type: 'image'
                        };
                        setAttachments(prev => [...prev, newAttachment]);
                      };
                      reader.readAsDataURL(file);
                      return false;
                    }}
                  >
                    <Button type="text" size="small" icon={<PictureOutlined />} />
                  </Upload>
                </Tooltip>
                <Tooltip title={t('upload_audio', '上传音频')}>
                  <Upload
                    accept="audio/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const newAttachment = {
                        uid: Date.now(),
                        name: file.name,
                        type: 'audio',
                        file: file
                      };
                      setAttachments(prev => [...prev, newAttachment]);
                      return false;
                    }}
                  >
                    <Button type="text" size="small" icon={<AudioOutlined />} />
                  </Upload>
                </Tooltip>
              </Space>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<NodeExpandOutlined />}
                  onClick={() => setDataFlowVisible(true)}
                  style={{ background: '#722ed1', borderColor: '#722ed1' }}
                >
                  {t('data_flow', '数据流')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={loading ? <LoadingOutlined /> : <PlayCircleOutlined />}
                  onClick={loading ? handleCancel : handleSend}
                  disabled={!input.trim() && attachments.length === 0 && !loading}
                >
                  {loading ? t('cancel', '取消') : t('send', '发送')}
                </Button>
              </Space>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 保存训练模型模态框 */}
      <Modal
        title={t('save_training_model', '保存训练模型')}
        open={saveModalVisible}
        onOk={handleSaveAsTrainedModel}
        onCancel={() => {
          setSaveModalVisible(false);
          resetSaveForm();
        }}
        okText={t('save', '保存')}
        cancelText={t('cancel', '取消')}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t('model_name', '模型名称')}</Text>
          <Input
            style={{ marginTop: 4 }}
            placeholder={t('enter_model_name', '请输入模型名称')}
            value={saveForm.name}
            onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t('description', '描述')}</Text>
          <Input.TextArea
            style={{ marginTop: 4 }}
            placeholder={t('enter_description', '描述这个配置的用途（可选）')}
            rows={2}
            value={saveForm.description}
            onChange={(e) => setSaveForm({ ...saveForm, description: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t('base_model', '基础模型')}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">{selectedModel || '-'}</Tag>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t('current_params', '当前参数')}</Text>
          <div style={{ marginTop: 4, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 12 }}>
            <div>Temperature: {params.temperature}</div>
            <div>Max Tokens: {params.max_tokens}</div>
            <div>Top P: {params.top_p}</div>
          </div>
        </div>
      </Modal>

      {/* 数据流侧边栏 */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          right: dataFlowVisible ? 0 : '-450px',
          width: 450,
          height: '100vh',
          background: '#fff',
          boxShadow: dataFlowVisible ? '-2px 0 8px rgba(0,0,0,0.15)' : 'none',
          transition: 'right 0.3s ease',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 侧边栏头部 */}
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#722ed1',
          color: '#fff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BranchesOutlined />
            <span style={{ fontWeight: 500 }}>{t('data_flow', '数据流')}</span>
          </div>
          <Button 
            type="text" 
            icon={<CloseCircleOutlined />} 
            onClick={() => setDataFlowVisible(false)}
            style={{ color: '#fff' }}
          />
        </div>

        {/* 侧边栏内容 */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {/* 请求配置 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6, 
              marginBottom: 10,
              fontSize: 13,
              fontWeight: 500,
              color: '#722ed1'
            }}>
              <ArrowRightOutlined />
              <span>{t('request_config', '请求配置')}</span>
            </div>
            <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, fontSize: 12 }}>
              {/* 模型 */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('model', '模型')}</Text>
                <div style={{ marginTop: 4 }}>
                  <Tag color="blue">{selectedModel || '-'}</Tag>
                </div>
              </div>
              
              {/* 训练配置 */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('trained_config', '训练配置')}</Text>
                <div style={{ marginTop: 4 }}>
                  {userTrainedConfig ? (
                    <Tag color="purple">{userTrainedConfig.name}</Tag>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('not_used', '未使用')}</Text>
                  )}
                </div>
              </div>
              
              {/* 预设角色 */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('preset', '预设角色')}</Text>
                <div style={{ marginTop: 4 }}>
                  {selectedPreset ? (
                    <Tag color="green">{selectedPreset.name}</Tag>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('not_used', '未使用')}</Text>
                  )}
                </div>
              </div>
              
              {/* 实际参数（与请求体一致） */}
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('actual_params', '实际参数')}</Text>
                <div style={{ 
                  marginTop: 4, 
                  padding: 8, 
                  background: '#fff', 
                  borderRadius: 4, 
                  fontSize: 11, 
                  fontFamily: 'monospace'
                }}>
                  <div><span style={{ color: '#666' }}>temperature:</span> {params.temperature}</div>
                  <div><span style={{ color: '#666' }}>max_tokens:</span> {params.max_tokens}</div>
                  <div><span style={{ color: '#666' }}>top_p:</span> {params.top_p}</div>
                  <div><span style={{ color: '#666' }}>frequency_penalty:</span> {params.frequency_penalty}</div>
                  <div><span style={{ color: '#666' }}>presence_penalty:</span> {params.presence_penalty}</div>
                  <div><span style={{ color: '#666' }}>stream:</span> {params.stream ? 'true' : 'false'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* System Prompt */}
          {systemPrompt && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                marginBottom: 10,
                fontSize: 13,
                fontWeight: 500,
                color: '#722ed1'
              }}>
                <ArrowRightOutlined />
                <span>System Prompt</span>
              </div>
              <div style={{ 
                padding: 12, 
                background: '#fafafa', 
                borderRadius: 8, 
                fontSize: 11, 
                fontFamily: 'monospace',
                maxHeight: 150,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {systemPrompt}
              </div>
            </div>
          )}

          {/* 实际请求体 */}
          {requestData && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                marginBottom: 10,
                fontSize: 13,
                fontWeight: 500,
                color: '#722ed1'
              }}>
                <ArrowRightOutlined />
                <span>{t('actual_request_body', '实际请求体')}</span>
              </div>
              <pre style={{ 
                padding: 12, 
                background: '#1e1e1e', 
                borderRadius: 8, 
                fontSize: 10, 
                fontFamily: 'monospace',
                color: '#d4d4d4',
                maxHeight: 200,
                overflow: 'auto',
                whiteSpace: 'pre-wrap'
              }}>
                {JSON.stringify(requestData, null, 2)}
              </pre>
            </div>
          )}

          {/* 响应数据 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6, 
              marginBottom: 10,
              fontSize: 13,
              fontWeight: 500,
              color: '#722ed1'
            }}>
              <ArrowRightOutlined />
              <span>{t('response_data', '响应数据')}</span>
            </div>
            {responseData ? (
              <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, fontSize: 12 }}>
                {responseData.error ? (
                  <div style={{ color: '#ff4d4f', padding: 8, background: '#fff1f0', borderRadius: 4 }}>
                    <Text strong>{t('error', '错误')}: </Text>
                    {responseData.error}
                  </div>
                ) : (
                  <>
                    {/* 用量信息 */}
                    {(responseData.usage || currentUsage) && (
                      <div style={{ marginBottom: 10 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('usage_info', '用量信息')}</Text>
                        <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <Tag color="cyan">
                            {t('input_tokens', '输入 Tokens')}: {(responseData.usage?.prompt_tokens || currentUsage?.prompt_tokens || 0)}
                          </Tag>
                          <Tag color="purple">
                            {t('output_tokens', '输出 Tokens')}: {(responseData.usage?.completion_tokens || currentUsage?.completion_tokens || 0)}
                          </Tag>
                          <Tag color="orange">
                            {t('total_tokens', '总 Tokens')}: {(responseData.usage?.total_tokens || currentUsage?.total_tokens || 0)}
                          </Tag>
                        </div>
                      </div>
                    )}
                    {/* 响应内容 */}
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>{t('response_content', '响应内容')}</Text>
                      <div style={{ 
                        marginTop: 4, 
                        padding: 12, 
                        background: '#fff', 
                        borderRadius: 4, 
                        maxHeight: 200, 
                        overflow: 'auto', 
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word',
                        fontSize: 11
                      }}>
                        {responseData.content || t('empty_response', '(空响应)')}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div style={{ padding: 24, background: '#fafafa', borderRadius: 8, color: '#999', textAlign: 'center' }}>
                <MessageOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                <div>{t('no_response_yet', '暂无响应数据，请发送消息后查看')}</div>
              </div>
            )}
          </div>

          {/* 说明 */}
          <div style={{ padding: 12, background: '#e6f7ff', borderRadius: 8, fontSize: 11 }}>
            <Text type="secondary">
              💡 {t('data_flow_tip', '此面板显示实际发送的请求参数和响应数据，方便调试和验证是否使用了正确的训练参数和预设角色。')}
            </Text>
          </div>
        </div>
      </div>

      {/* 遮罩层 */}
      {dataFlowVisible && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 999
          }}
          onClick={() => setDataFlowVisible(false)}
        />
      )}
    </div>
  );
};

export default Playground;
